import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Box, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type SystemChipItem, SystemChips } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { ColorDot } from "../../../components/UIToolkit/molecules/ColorDot";
import { useJobColors } from "../../../hooks/useJobColors";
import { ALPHA, BADGE, FONT_SIZE, ICON_SIZE, INK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { formatNameList } from "../../../utils/formatNameList";
import type { MyFacilityJobNotifications } from "../../../utils/jobNotificationTypes";

type Props = {
	facility: MyFacilityJobNotifications;
	/** The facility name shown above the chips. Set only when the employee has more than one facility. */
	facilityName?: string;
	/** The screen's card style, so this section matches the channel card above. */
	cardSx: SystemStyleObject<Theme>;
	/** The latest failed save's text for this facility, if any. */
	saveErrorText?: string;
	onToggle: (item: SystemChipItem<string>, selected: boolean) => void;
};

// The dot is a plain element, not an MUI Avatar, so its spacing is set here.
const CHIP_BASE: SystemStyleObject<Theme> = {
	borderRadius: BADGE.PILL_RADIUS_FULL,
	fontSize: FONT_SIZE.SECONDARY,
	"& .MuiChip-label": { pl: 0.75, pr: 1.25 },
};
const selectedSx: SystemStyleObject<Theme> = {
	"&.MuiChip-root": {
		...CHIP_BASE,
		backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, ALPHA.BRAND_TINT),
		borderColor: "primary.main",
		color: "primary.main",
		fontWeight: 600,
	},
	"&.MuiChip-root:hover": { backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER), color: "primary.main" },
};
const unselectedSx: SystemStyleObject<Theme> = {
	"&.MuiChip-root": { ...CHIP_BASE, backgroundColor: SURFACE, borderColor: NEUTRAL[400], color: INK.SECONDARY, fontWeight: 500 },
	"&.MuiChip-root:hover": { backgroundColor: SURFACE, borderColor: "primary.main", color: INK.SECONDARY },
};
const noteSx = { fontSize: FONT_SIZE.CAPTION, lineHeight: 1.45, px: 0.5, pt: 1 } as const;

/** One facility's job chips and the notes under them. A chip is ticked while its job notifies. */
export const FacilityJobChips: FC<Props> = ({ facility, facilityName, cardSx, saveErrorText, onToggle }) => {
	const { t, i18n } = useTranslation();
	const { nurseId, jobs } = facility;
	const formatList = (names: string[]) => formatNameList(names, i18n.language);
	const jobColor = useJobColors();

	const items = useMemo<SystemChipItem<string>[]>(
		() =>
			jobs.map((job) => ({
				value: job.jobId,
				label: job.displayName,
				startAdornment: <ColorDot color={jobColor(job.jobId).dot} sx={{ ml: 1.25 }} />,
				endAdornment: <CheckRoundedIcon aria-hidden sx={{ fontSize: ICON_SIZE.PILL, opacity: job.isSilent ? 0 : 1 }} />,
			})),
		[jobs, jobColor],
	);
	const selectedItems = useMemo(() => items.filter((item) => jobs.some((job) => job.jobId === item.value && !job.isSilent)), [items, jobs]);

	const onNames = jobs.filter((job) => !job.isSilent).map((job) => job.displayName);
	const offNames = jobs.filter((job) => job.isSilent).map((job) => job.displayName);
	const schedulerOffNames = jobs.filter((job) => job.isSilent && job.turnedOffByScheduler).map((job) => job.displayName);
	const allOffText = facilityName ? t("jobNotifications.bedside.allOffAtFacility", { facility: facilityName }) : t("jobNotifications.bedside.allOff");

	return (
		<Box data-testid={`which-shifts-facility-${nurseId}`} sx={{ mt: facilityName ? 1.5 : 0 }}>
			{facilityName && (
				<Typography
					component="h3"
					data-testid={`which-shifts-facility-name-${nurseId}`}
					sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.SECONDARY, px: 2, pb: 0.5 }}
				>
					{facilityName}
				</Typography>
			)}
			<Box sx={{ ...cardSx, px: 1.5, py: 1.5 }}>
				<SystemChips
					items={items}
					selectedItems={selectedItems}
					onChange={onToggle}
					variant="outlined"
					selectedSx={selectedSx}
					unselectedSx={unselectedSx}
					rootSx={{ justifyContent: "flex-start" }}
					forceFitContent
					touchTarget
					announcePressed
					dataTestId={`which-shifts-chips-${nurseId}`}
					getItemDataTestId={(item) => `which-shifts-chip-${nurseId}-${item.value}`}
				/>
			</Box>
			<Box sx={{ mx: 2 }}>
				{saveErrorText && (
					<Typography role="alert" data-testid={`which-shifts-save-error-${nurseId}`} sx={{ ...noteSx, color: "error.dark" }}>
						{saveErrorText}
					</Typography>
				)}
				{onNames.length === 0 && (
					<Typography role="status" data-testid={`which-shifts-all-off-${nurseId}`} sx={{ ...noteSx, color: "warning.dark" }}>
						{allOffText}
					</Typography>
				)}
				{onNames.length > 0 && (
					<Typography sx={{ ...noteSx, color: INK.MUTED }}>
						{offNames.length === 0
							? t("jobNotifications.bedside.allOn")
							: t("jobNotifications.bedside.someOn", { onJobs: formatList(onNames), offJobs: formatList(offNames) })}
					</Typography>
				)}
				{schedulerOffNames.length > 0 && (
					<Typography data-testid={`which-shifts-scheduler-note-${nurseId}`} sx={{ ...noteSx, color: INK.SECONDARY }}>
						{t("jobNotifications.bedside.turnedOffByScheduler", { jobs: formatList(schedulerOffNames), count: schedulerOffNames.length })}
					</Typography>
				)}
			</Box>
		</Box>
	);
};
